import { useCallback } from 'react';
import { shallow } from 'zustand/shallow';
import { useStoreWithEqualityFn } from 'zustand/traditional';

import { selectData, selectById } from '../selectors.ts';
import { useAttach } from '../useAttach';

import { imagesStore, type ImagesStoreState } from './ImagesStore';

export function useImagesData() {
  useAttach(imagesStore, 'useImagesData');
  return useStoreWithEqualityFn(imagesStore.store, selectData, shallow);
}
useImagesData.store = imagesStore;

export function useImagesById() {
  useAttach(imagesStore, 'useImagesById');
  return useStoreWithEqualityFn(imagesStore.store, selectById, shallow);
}
useImagesById.store = imagesStore;

export function useImage({ imageId }: { imageId?: string }) {
  useAttach(imagesStore, 'useImage');

  const select = useCallback(
    (state: ImagesStoreState) => {
      if (!imageId) {
        return {
          image: null,
          imageInstance: null,
        };
      }

      return {
        image: state.byId?.[imageId] ?? null,
        imageInstance: state.data?.[imageId] ?? null,
      };
    },
    [imageId],
  );

  return useStoreWithEqualityFn(imagesStore.store, select, shallow);
}
useImage.store = imagesStore;

export function useHomeyImage() {
  useAttach(imagesStore, 'useHomeyImage');

  const select = useCallback((state: ImagesStoreState) => {
    const homeyImage = Object.values(state.byId ?? {}).find((image) => {
      return image.ownerUri === 'homey:manager:images';
    });

    return {
      image: homeyImage ?? null,
    };
  }, []);

  return useStoreWithEqualityFn(imagesStore.store, select, shallow);
}
useHomeyImage.store = imagesStore;

export function useImagesAttach() {
  useAttach(imagesStore, 'useImagesAttach');
}
useImagesAttach.store = imagesStore;
